/* eslint-disable react/no-danger */
import { appendQueryParams, contestSongToCreatePlayable, createAppRoutes, getSignupRedirectLink, HLSPlayer, State, useHubSwitch, usePlayerState, useUserSettings } from '@bpm-web-app/utils';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useContests } from '@bpm-web-app/swr-hooks';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import styles from './contest-detail.module.css';
import GenericCarousel from '../../shared/ui/generic-carousel/generic-carousel';
import PrizeCard from '../../prize-card/prize-card';
import SmallPlay from '../../../assets/icons/create/small-play.svg';
import Submissions from '../../submissions/submissions';
import useContest from '../../shared/three-dots-sheet/useContest';
import { useCreatePlayer } from '../../../../../utils/src/lib/create-player.context';
import { AppLink } from '../../shared/app-link/app-link';
import CardCreate from '../../shared/card/card-create';
import PauseIcon from '../../../assets/icons/player-pause.svg';
import { ShareURL } from '../../shared/share-url/share-url';
import { PageNotFound } from '../page-not-found/page-not-found';
import Title from '../../title/title';
import { ActionModal } from '../../shared/action-modal/action-modal';
import { Checkbox } from '../../shared/ui';
import { ContestTermsPreview } from '../../shared/contest-terms-preview/contest-terms-preview';

const MAX_DESCRIPTION_CHARACTERS = 590;
const SHOW_SUBMISSIONS = false;
const SHOW_RELATED_CONTESTS = false;
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ContestDetailProps {
    contestBasePath?: string;
    contestDetailPath?: (slug: string) => string;
}

type ContestPrizeDetails = {
    prize_display_type?: 'carousel' | 'text';
    prize_details?: string;
};

export function ContestDetail({ contestBasePath = createAppRoutes.contest, contestDetailPath = createAppRoutes.contestDetail }: ContestDetailProps) {
    const router = useRouter();
    const { slug } = router.query;
    const { contests, isLoading, error } = useContests();
    const [showTotalDescription, setShowTotalDescription] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [showTermsError, setShowTermsError] = useState(false);
    const playerState = usePlayerState();
    const { hub } = useHubSwitch();
    const { isAnonymous } = useUserSettings();

    const { playDemo, currentTrack, togglePlayPause } = useCreatePlayer();

    const { isOpen } = useCreatePlayer();

    const detailedContest = useMemo(() => {
        const contest = contests?.current?.find((contestObj) => contestObj.slug === slug);
        if (contest) {
            return contest;
        }
        return contests?.past?.find((contestObj) => contestObj.slug === slug);
    }, [contests, slug]);

    const isExpired = useMemo(() => dayjs(detailedContest?.submission_deadline).isBefore(new Date()), [detailedContest]);

    const isCurrentContestPlaying = useMemo(() => currentTrack?.name === detailedContest?.title, [currentTrack?.name, detailedContest?.title]);
    const detailedContestWithPrizeDetails = detailedContest as (NonNullable<typeof detailedContest> & ContestPrizeDetails) | undefined;
    const showPrizeDetails = detailedContestWithPrizeDetails?.prize_display_type === 'text';

    const { contestReportDownloadStems } = useContest(detailedContest?.id || '', true);

    const downloadStem = useCallback(() => {
        if (detailedContest?.zip_url && !isExpired) {
            setShowTermsModal(true);
            setShowTermsError(false);
        }
    }, [detailedContest?.zip_url, isExpired]);

    const closeTermsModal = useCallback(() => {
        setShowTermsModal(false);
        setAcceptedTerms(false);
        setShowTermsError(false);
    }, []);

    const confirmDownloadStem = useCallback(() => {
        if (!acceptedTerms) {
            setShowTermsError(true);
            return;
        }

        if (detailedContest?.zip_url && !isExpired) {
            contestReportDownloadStems();
            window.location.assign(detailedContest.zip_url);
            closeTermsModal();
        }
    }, [acceptedTerms, closeTermsModal, contestReportDownloadStems, detailedContest?.zip_url, isExpired]);

    const play = useCallback(() => {
        if (detailedContest?.song_url) {
            if (!isCurrentContestPlaying) {
                playDemo(contestSongToCreatePlayable(detailedContest));
            } else {
                togglePlayPause();
            }
        }
    }, [detailedContest, isCurrentContestPlaying, playDemo, togglePlayPause]);

    const getDescriptionMarkup = (text: string) => <div className={styles['contest-detail__date']}>{text}</div>;

    useEffect(
        () => () => {
            if (!isOpen) HLSPlayer.destroy();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => {
        if (isAnonymous) {
            router.replace(getSignupRedirectLink());
        }
    }, [isAnonymous, router]);

    /* TODO: implement (ghost?) loading */
    if (isLoading || isAnonymous) return <div />;

    /* TODO: potentially replace this with a custom 404 page if/when built */
    if (error || !detailedContest) return <PageNotFound />;

    const currentContests = contests?.current ?? [];
    const pastContests = contests?.past ?? [];

    return (
        <>
            <Title platform={hub} title={detailedContest?.title} />
            <ActionModal
                headerTitle="Terms & Conditions"
                confirmButtonText="Download Stems"
                cancelButtonText="Cancel"
                variant="dark"
                isOpen={showTermsModal}
                onClose={closeTermsModal}
                onConfirm={confirmDownloadStem}
                shouldCloseOnOverlayClick
            >
                <div className={styles['contest-detail__terms-modal']}>
                    <p className={styles['contest-detail__terms-modal-text']}>
                        Before downloading the stems, please agree to the contest terms and conditions.
                    </p>
                    {detailedContest.terms_pdf_url && (
                        <ContestTermsPreview pdfUrl={detailedContest.terms_pdf_url} linkClassName={styles['contest-detail__terms-modal-link']} />
                    )}
                    <div className={styles['contest-detail__terms-modal-checkbox']}>
                        <Checkbox
                            checked={acceptedTerms}
                            onChange={(event) => {
                                setAcceptedTerms(event.target.checked);
                                setShowTermsError(false);
                            }}
                        />
                        <button
                            type="button"
                            className={styles['contest-detail__terms-modal-checkbox-text']}
                            onClick={() => {
                                setAcceptedTerms((currentAcceptedTerms) => !currentAcceptedTerms);
                                setShowTermsError(false);
                            }}
                        >
                            I agree to the contest terms and conditions.
                        </button>
                    </div>
                    {showTermsError && <p className={styles['contest-detail__terms-modal-error']}>You must agree to the terms and conditions before downloading stems.</p>}
                </div>
            </ActionModal>
            <div className={styles['contest-detail']}>
                {detailedContest.banner_image_url && (
                    <div className={styles['contest-detail__img-wrapper']}>
                        <picture className={styles['contest-detail__img']}>
                            <source srcSet={detailedContest.banner_image_url} media="(min-width: 1920px)" />
                            <source
                                srcSet={`${appendQueryParams(detailedContest.banner_image_url, { key: 'dw', value: 3840 })} 2x, ${appendQueryParams(detailedContest.banner_image_url, { key: 'dw', value: 1920 })} 1x`}
                                media="(min-width: 1440px)"
                            />
                            <source
                                srcSet={`${appendQueryParams(detailedContest.banner_image_url, { key: 'dw', value: 2880 })} 2x, ${appendQueryParams(detailedContest.banner_image_url, { key: 'dw', value: 1440 })} 1x`}
                                media="(min-width: 1024px)"
                            />
                            <source
                                srcSet={`${appendQueryParams(detailedContest.banner_image_url, { key: 'dw', value: 2048 })} 2x, ${appendQueryParams(detailedContest.banner_image_url, { key: 'dw', value: 1024 })} 1x`}
                                media="(min-width: 768px)"
                            />
                            <img
                                src={appendQueryParams(detailedContest.banner_image_url, { key: 'dw', value: 1024 })}
                                srcSet={`${appendQueryParams(detailedContest.banner_image_url, { key: 'dw', value: 2048 })} 2x`}
                                alt={detailedContest?.title || ''}
                            />
                        </picture>
                    </div>
                )}
                <div className={styles['contest-detail__intro']}>
                    <div className={styles['contest-detail__summary']}>
                        <h1 className={styles['contest-detail__title']}>{detailedContest?.title || ''}</h1>
                        <div className={styles['contest-detail__summary-ctas']}>
                            <div className={styles['contest-detail__summary-ctas-primary']}>
                                {isExpired ?
                                    <div className={classNames(styles['contest-detail__summary-cta'], styles['contest-detail__summary-cta--primary'], styles['contest-detail__summary-cta--text'], styles['contest-detail__summary-cta--disabled'])}>
                                        Submit Your Remix
                                    </div>
                                    : (
                                        <AppLink href={`${contestBasePath}/${slug}/submit-remix`}>
                                            <a className={classNames(styles['contest-detail__summary-cta'], styles['contest-detail__summary-cta--primary'], styles['contest-detail__summary-cta--text'])}>
                                                Submit Your Remix
                                            </a>
                                        </AppLink>
                                    )
                                }
                                <button
                                    type="button"
                                    disabled={isExpired}
                                    className={classNames(styles['contest-detail__summary-cta'], styles['contest-detail__summary-cta--secondary'], styles['contest-detail__summary-cta--text'])}
                                    onClick={downloadStem}
                                >
                                    Download Stems
                                </button>
                            </div>
                            <div className={styles['contest-detail__summary-ctas-actions']}>
                                <button
                                    type="button"
                                    className={styles['contest-detail__icon-btn']}
                                    onClick={play}
                                    aria-label="Preview Original Track"
                                    data-tip="Preview Original Track"
                                >
                                    {isCurrentContestPlaying && playerState === State.Playing ? <PauseIcon /> : <SmallPlay />}
                                </button>
                                <ShareURL currentUrl />
                            </div>
                        </div>
                        <ul className={styles['contest-detail__details']}>
                            <li className={styles['contest-detail__detail']}>
                                Submission Start Date
                                <strong>{dayjs(detailedContest.start_date).format('MMMM D, YYYY, hh:mma')}</strong>
                            </li>
                            <li className={styles['contest-detail__detail']}>
                                Submission Deadline
                                <strong>{dayjs(detailedContest.submission_deadline).format('MMMM D, YYYY, hh:mma')}</strong>
                            </li>
                            <li className={styles['contest-detail__detail']}>
                                Winner Announced
                                <strong>{dayjs(detailedContest.announcement).format('MMMM D, YYYY, hh:mma')}</strong>
                            </li>
                        </ul>
                    </div>
                    <div className={styles['contest-detail__content']}>
                        <div
                            className={styles['contest-detail__description']}
                            dangerouslySetInnerHTML={{
                                __html:
                                    detailedContest.description.length > MAX_DESCRIPTION_CHARACTERS && !showTotalDescription
                                        ? detailedContest.description.slice(0, MAX_DESCRIPTION_CHARACTERS)
                                        : detailedContest.description,
                            }}
                        />
                        {detailedContest.description.length > MAX_DESCRIPTION_CHARACTERS && !showTotalDescription && (
                            <button type="button" onClick={() => setShowTotalDescription(true)} className={styles['contest-detail__link']}>
                                See Rules
                            </button>
                        )}
                    </div>
                </div>

                {showPrizeDetails ? (
                    detailedContestWithPrizeDetails?.prize_details && (
                        <section className={styles['contest-detail__prize-details']}>
                            <h2 className={styles['contest-detail__prize-details-title']}>Prizes</h2>
                            <div className={styles['contest-detail__prize-details-copy']} dangerouslySetInnerHTML={{ __html: detailedContestWithPrizeDetails.prize_details }} />
                        </section>
                    )
                ) : (
                    <GenericCarousel title="Prizes" cssMode noPadding>
                        {detailedContest.ContestPrizes.map((prize) => (
                            <Fragment key={prize.id}>
                                <PrizeCard id={prize.id} image={prize.image_url} title={prize.title} description={prize.description} link={prize.link} />
                            </Fragment>
                        ))}
                    </GenericCarousel>
                )}

                {SHOW_SUBMISSIONS && <Submissions contestArtworkUrl={detailedContest.artwork_url} contestId={detailedContest.id} contestTitle={detailedContest.title} />}

                {SHOW_RELATED_CONTESTS && currentContests.length > 0 &&
                    <GenericCarousel title="Current Contests" noPadding>
                        {currentContests.map((item) => (
                            <CardCreate
                                contentType="contest"
                                cardSize="large"
                                key={item.id}
                                id={`${item.id}`}
                                title={item.title}
                                imageUrl={appendQueryParams(item.artwork_url, { key: 'dw', value: 160 })}
                                imageUrl2x={appendQueryParams(item.artwork_url, { key: 'dw', value: 320 })}
                                link={item.slug ? contestDetailPath(item.slug) : undefined}
                                description={getDescriptionMarkup(`Deadline ${dayjs(item.submission_deadline).tz('America/Los_Angeles').format('MMMM DD,YYYY hh:mm')} PST`)}
                            />
                        ))}
                    </GenericCarousel>
                }
                {SHOW_RELATED_CONTESTS && (
                    <GenericCarousel title="Past Contests" noPadding>
                        {pastContests.map((item) => (
                            <CardCreate
                                contentType="contest"
                                cardSize="large"
                                key={item.id}
                                id={`${item.id}`}
                                title={item.title}
                                description={getDescriptionMarkup(`Expired ${dayjs(item.submission_deadline).tz('America/Los_Angeles').format('MMMM DD,YYYY')}`)}
                                imageUrl={appendQueryParams(item.artwork_url, { key: 'dw', value: 160 })}
                                imageUrl2x={appendQueryParams(item.artwork_url, { key: 'dw', value: 320 })}
                                link={item.slug ? contestDetailPath(item.slug) : undefined}
                            />
                        ))}
                    </GenericCarousel>
                )}
            </div>
        </>
    );
}

export default ContestDetail;
/* eslint-enable react/no-danger */
